import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BaseView, BaseText } from '@/components/base'
import { AppButton } from '@/components/AppButton'
import { useIdentityVerification } from '@/domain/identity/useCases'
import { useCheckTransactionEligibility } from '@/hooks'
import { responsiveValueAdapter } from '@/utils'

interface WithoutKemCardProps {
  isLoadingKemCardState?: boolean
}

export const WithoutKemCard: React.FC<WithoutKemCardProps> = ({
  isLoadingKemCardState,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isPending: isStartingKYC } = useIdentityVerification()
  const { checkTransactionEligibility } = useCheckTransactionEligibility({
    feature: 'card',
  })

  const handleGoToInfoConfirmation = async () => {
    const isEligible = await checkTransactionEligibility()

    if (!isEligible) {
      return
    }

    navigate('/kem-card/info-confirmation')
  }

  return (
    <BaseView
      className="h-screen flex flex-col overflow-hidden"
      data-component="send-wrapper"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        overscrollBehavior: 'none',
        height: '100vh',
        maxHeight: '100vh',
      }}
    >
      <BaseView>
        {/* Gradient background */}
        {/* <SendBackground /> */}
        <BaseView
          className="fixed inset-0 z-0"
          style={{
            marginTop: 40,
            backgroundImage: 'url(/assets/images/KemCard.png)',
            backgroundSize: 'contain',
            backgroundPosition: '50% 50%',
            backgroundRepeat: 'no-repeat',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
          }}
        />
      </BaseView>

      <BaseView
        className="absolute inset-0 z-10 flex flex-col justify-end"
        px={12}
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
          paddingBottom: responsiveValueAdapter(80, true),
          overflow: 'hidden',
          overscrollBehavior: 'none',
        }}
      >
        <BaseView
          p={20}
          align="center"
          gap={42}
          style={{
            overflow: 'hidden',
            flexShrink: 0,
            width: '100%',
          }}
        >
          {/* Background Image Placeholder */}

          <BaseView gap={6} align="center">
            <BaseText
              fontSize={28}
              style={{
                whiteSpace: 'nowrap',
              }}
              fontFamily="semibold"
              color="label.default"
              textAlign="center"
            >
              {t('translation:spendCryptoLiveLimitless')}
            </BaseText>

            <BaseText
              fontSize={15}
              fontFamily="regular"
              color="label.default"
              textAlign="center"
            >
              {t('translation:kemCardDescription')}
            </BaseText>
          </BaseView>

          {/* Main button to navigate to info-confirmation */}
          <AppButton
            onPress={handleGoToInfoConfirmation}
            disabled={isLoadingKemCardState || isStartingKYC}
            isLoading={isLoadingKemCardState || isStartingKYC}
            label={
              isStartingKYC
                ? 'Starting Verification...'
                : isLoadingKemCardState
                  ? 'Loading...'
                  : t('translation:kemCard.activateCard')
            }
            type="blur"
            style={{
              backgroundColor: '#8065FF',
            }}
          />
        </BaseView>
      </BaseView>
    </BaseView>
  )
}
